import React, { useState } from 'react';
import { UsersIcon, PlusIcon, EditIcon, UserCheckIcon, UserXIcon, ShieldIcon, UserIcon, AlertCircleIcon, Trash2Icon, LockIcon, SearchIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
export function UsersPage() {
  const {
    users,
    roles,
    addUser,
    updateUser,
    deleteUser
  } = useApp();
  const {
    addActivityLog
  } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    rolId: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      password: '',
      rolId: roles.length > 0 ? roles[0].id : ''
    });
    setErrors({});
    setEditingUser(null);
    setSubmitError('');
  };
  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };
  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      nombre: user.nombre,
      email: user.email,
      password: '',
      rolId: user.rolId
    });
    setSubmitError('');
    setErrors({});
    setIsModalOpen(true);
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.rolId) newErrors.rolId = 'El rol es requerido';
    if (!editingUser && !formData.password) newErrors.password = 'La contraseña es requerida para nuevos usuarios';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      if (editingUser) {
        const updates: Partial<User> = {
          nombre: formData.nombre,
          email: formData.email,
          rolId: formData.rolId
        };
        if (formData.password) updates.password = formData.password;
        const result = await updateUser(editingUser.id, updates);
        if (!result.success) throw new Error(result.error);
        addActivityLog('Editó usuario', `Usuario: ${formData.nombre} | Rol: ${roles.find(r => r.id === formData.rolId)?.nombre || ''}`);
      } else {
        const result = await addUser({
          nombre: formData.nombre,
          email: formData.email,
          password: formData.password,
          rolId: formData.rolId,
          activo: true
        });
        if (!result.success) throw new Error(result.error);
        addActivityLog('Creó usuario', `Usuario: ${formData.nombre} | Rol: ${roles.find(r => r.id === formData.rolId)?.nombre || ''}`);
      }
      setIsModalOpen(false);
      resetForm();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Error al guardar usuario');
    } finally {
      setIsSubmitting(false);
    }
  };
  const toggleUserStatus = async (user: User) => {
    await updateUser(user.id, {
      activo: !user.activo
    });
    addActivityLog(user.activo ? 'Desactivó usuario' : 'Activó usuario', `Usuario: ${user.nombre}`);
  };
  const handleDelete = async (user: User) => {
    if (!confirm(`¿Eliminar a ${user.nombre}? Esta acción no se puede deshacer.`)) return;
    await deleteUser(user.id);
    addActivityLog('Eliminó usuario', `Usuario: ${user.nombre}`);
  };
  const roleOptions = roles.map(r => ({
    value: r.id,
    label: r.nombre
  }));
  const activeUsers = users.filter(u => u.activo);
  const inactiveUsers = users.filter(u => !u.activo);
  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return u.nombre.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.rolNombre.toLowerCase().includes(q);
  });
  const getUserAvatar = (userId: string): string => {
    try {
      return localStorage.getItem(`ventastrack_avatar_${userId}`) || '';
    } catch {
      return '';
    }
  };
  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return <div className="space-y-6" data-id="element-3785">
      <PageHeader title="Gestión de Usuarios" subtitle="Administración de vendedores y personal" icon={<UsersIcon className="w-6 h-6" data-id="element-3787" />} data-id="element-3786">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3789" />} onClick={openCreateModal} data-id="element-3788">
          Nuevo Usuario
        </Button>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-stagger-1" data-id="element-3790">
        <StatsCard title="Total Usuarios" value={users.length} icon={<UsersIcon className="w-5 h-5" data-id="element-3792" />} variant="primary" data-id="element-3791" />
        <StatsCard title="Usuarios Activos" value={activeUsers.length} icon={<UserCheckIcon className="w-5 h-5" data-id="element-3794" />} variant="success" data-id="element-3793" />
        <StatsCard title="Usuarios Inactivos" value={inactiveUsers.length} icon={<UserXIcon className="w-5 h-5" data-id="element-3796" />} variant="danger" data-id="element-3795" />
      </div>

      {/* Search */}
      <div className="relative max-w-md animate-stagger-2" data-id="element-3797">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-3798" />
        <Input placeholder="Buscar usuarios..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-3799" />
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-3800">
        <div className="px-6 py-5 border-b border-slate-100" data-id="element-3801">
          <CardHeader title="Lista de Usuarios" data-id="element-3802" />
        </div>
        <div className="divide-y divide-slate-100" data-id="element-3803">
          {filteredUsers.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-3804">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-3805">
                <UsersIcon className="w-8 h-8 text-slate-400" data-id="element-3806" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-3807">
                No hay usuarios
              </h3>
              <p className="text-slate-500 text-sm" data-id="element-3808">
                Crea el primer usuario con el botón de arriba
              </p>
            </div> : filteredUsers.map(user => <div key={user.id} className="px-6 py-4 hover:bg-slate-50/50 transition-colors" data-id="element-3809">
                <div className="flex items-center justify-between" data-id="element-3810">
                  <div className="flex items-center gap-4" data-id="element-3811">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0" style={{
                backgroundColor: user.avatarUrl ? 'transparent' : '#eef2fc'
              }} data-id="element-3812">
                      {user.avatarUrl ? <img src={user.avatarUrl} alt={user.nombre} className="w-full h-full object-cover" data-id="element-3813" /> : <span className="text-sm font-bold" style={{
                  color: '#2952C4'
                }} data-id="element-3814">
                          {getInitials(user.nombre)}
                        </span>}
                    </div>
                    <div data-id="element-3815">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-3816">
                        <h4 className="font-semibold text-slate-900 font-heading" data-id="element-3817">
                          {user.nombre}
                        </h4>
                        <Badge variant={user.activo ? 'success' : 'danger'} data-id="element-3818">
                          {user.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <Badge variant={user.rolNombre === 'Administrador' ? 'info' : 'default'} data-id="element-3819">
                          {user.rolNombre}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5" data-id="element-3820">
                        {user.email} • Creado:{' '}
                        {new Date(user.fechaCreacion).toLocaleDateString('es-DO')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-3821">
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(user)} data-id="element-3822">
                      <EditIcon className="w-4 h-4" data-id="element-3823" />
                    </Button>
                    <Button variant={user.activo ? 'danger' : 'success'} size="sm" onClick={() => toggleUserStatus(user)} data-id="element-3824">
                      {user.activo ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} data-id="element-3825">
                      <Trash2Icon className="w-4 h-4 text-rose-400" data-id="element-3826" />
                    </Button>
                  </div>
                </div>
              </div>)}
        </div>
      </Card>

      {/* Modal */}
      <Modal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      resetForm();
    }} title={editingUser ? 'Editar Usuario' : 'Nuevo Usuario'} data-id="element-3827">
        <ModalBody data-id="element-3828">
          <div className="space-y-4" data-id="element-3829">
            {submitError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-3830">
                <AlertCircleIcon className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" data-id="element-3831" />
                <p className="text-sm text-rose-700" data-id="element-3832">{submitError}</p>
              </div>}
            <Input label="Nombre Completo" placeholder="Ej. Juan Pérez" value={formData.nombre} onChange={e => setFormData({
            ...formData,
            nombre: e.target.value
          })} error={errors.nombre} data-id="element-3833" />
            <Input label="Email (para iniciar sesión)" placeholder="juan@empresa.com" type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} data-id="element-3834" />
            <Select label="Rol" options={roleOptions} value={formData.rolId} onChange={e => setFormData({
            ...formData,
            rolId: e.target.value
          })} error={errors.rolId} data-id="element-3835" />
            <Input label={editingUser ? 'Nueva Contraseña (dejar en blanco para mantener)' : 'Contraseña'} type="password" placeholder="••••••••" value={formData.password} onChange={e => setFormData({
            ...formData,
            password: e.target.value
          })} error={errors.password} icon={<LockIcon className="w-4 h-4 text-slate-400" data-id="element-3837" />} data-id="element-3836" />
          </div>
        </ModalBody>
        <ModalFooter data-id="element-3838">
          <Button variant="secondary" onClick={() => {
          setIsModalOpen(false);
          resetForm();
        }} data-id="element-3839">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} data-id="element-3840">
            {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}